/**
 * Declarative config of `extranet.homeQuickAccess` (Accueil « Accès rapide »
 * + « Flotte automobile » tiles, fiches 22062 / 22058, 22065, 22067, 22079).
 *
 * Every tile carries a **stable key** (`contrats`, `sinistres`, `primes`,
 * `vehicules`) — the contract a layout rule targets — separate from its
 * display `label`, which is overridable (rename / hide / reorder / tooltip
 * through the info-item bucket). Every section carries a stable key
 * (`accesRapide`, `flotte`) and its title lives here, never inline in JSX.
 *
 * The design's « Déclarer un sinistre », « Ajouter un véhicule » and « Sortir
 * un véhicule » tiles have no spec (plan "Out of scope") and are not part of
 * the config — no dead tile is rendered.
 */

import { AlertTriangle, Car, FileText, Receipt, type LucideIcon } from 'lucide-react';

import type { ResponsiveColumns } from '@components/base/layout';
import type { InfoItemDescriptor } from '@components/shared/configurable';

import { HOME_COUNTER_ENDPOINTS, type HomeCounterEndpoint } from '../../api/homeService';

/** Registered id of the tiles component. */
export const HOME_QUICK_ACCESS_ID = 'extranet.homeQuickAccess';

/** Stable key of a tile (rule contract). */
export type QuickAccessTileKey = 'contrats' | 'sinistres' | 'primes' | 'vehicules';

/** Stable key of a tile section (rule contract). */
export type QuickAccessSectionKey = 'accesRapide' | 'flotte';

/** One tile of the Accueil (declarative config). */
export interface QuickAccessTileDescriptor extends InfoItemDescriptor {
  /** Stable key — the rule contract (never the label). */
  key: QuickAccessTileKey;
  /** Display label (overridable with a rename rule). */
  label: string;
  /** Text after the count (« 8 en cours », « 8 à régler »). */
  countLabel: string;
  icon: LucideIcon;
  /** `WS2_EA_CTR_*` endpoint whose `NB` is the tile's count. */
  endpoint: HomeCounterEndpoint;
  /** Sub-path under `/extranet/:tenantSlug` the tile opens. */
  to: string;
  /** Section the tile belongs to. */
  section: QuickAccessSectionKey;
  /** Sort order inside its section (lower first). */
  order: number;
  /** `false` ⇒ hidden at baseline (revealable by a rule). */
  visible?: boolean;
  tooltip?: string;
}

/** One tile section (declarative config). */
export interface QuickAccessSection {
  /** Stable key — the rule contract of the `sections` bucket. */
  key: QuickAccessSectionKey;
  /** Section heading. */
  title: string;
  /** Section order (lower first). */
  order: number;
  /** Responsive grid columns of the section's tiles (design widths). */
  columns: ResponsiveColumns;
}

/** The four counter tiles of the Accueil, in design order. */
export const QUICK_ACCESS_TILES: readonly QuickAccessTileDescriptor[] = [
  {
    key: 'contrats',
    label: 'Contrats',
    countLabel: 'en cours',
    icon: FileText,
    endpoint: HOME_COUNTER_ENDPOINTS.contrats,
    to: 'contrats',
    section: 'accesRapide',
    order: 0,
  },
  {
    key: 'sinistres',
    label: 'Sinistres',
    countLabel: 'en cours',
    icon: AlertTriangle,
    endpoint: HOME_COUNTER_ENDPOINTS.sinistres,
    to: 'sinistres',
    section: 'accesRapide',
    order: 1,
  },
  {
    key: 'primes',
    label: 'Primes',
    countLabel: 'à régler',
    icon: Receipt,
    endpoint: HOME_COUNTER_ENDPOINTS.primes,
    to: 'primes',
    section: 'accesRapide',
    order: 2,
  },
  {
    key: 'vehicules',
    label: 'Véhicules',
    countLabel: 'en cours',
    icon: Car,
    endpoint: HOME_COUNTER_ENDPOINTS.vehicules,
    to: 'flotte',
    section: 'flotte',
    order: 3,
  },
];

/**
 * The two sections. The heading of `accesRapide` doubles as the component
 * title (`setTitle` renames it — see `QuickAccessTiles`).
 */
export const QUICK_ACCESS_SECTIONS: readonly QuickAccessSection[] = [
  { key: 'accesRapide', title: 'Accès rapide', order: 0, columns: { base: 1, sm: 2, lg: 4 } },
  { key: 'flotte', title: 'Flotte automobile', order: 1, columns: { base: 1, sm: 2, lg: 3 } },
];

/** Key of the section whose heading is the component title. */
export const QUICK_ACCESS_TITLE_SECTION: QuickAccessSectionKey = 'accesRapide';
